import { useNavigate } from 'react-router-dom'
import { Calendar, ChevronRight } from 'lucide-react'
import clsx from 'clsx'
import { useVehicles, useSchedules } from '../api/hooks'
import { getScheduleStatus, STATUS_STYLES } from '../utils/schedule'

function Plate({ registration }) {
  return (
    <span className="inline-block font-mono font-bold tracking-widest text-xs
                     bg-yellow-400 text-black px-2 py-0.5 rounded">
      {registration}
    </span>
  )
}

function ScheduleSummaryRow({ schedule, vehicle }) {
  const { status, daysUntil, milesUntil } = getScheduleStatus(schedule, vehicle.current_mileage)
  const styles = STATUS_STYLES[status]
  const name   = schedule.item_name || schedule.custom_name || 'Service'

  let dueText = 'No due date'
  if (schedule.next_due_date) {
    const d = new Date(schedule.next_due_date)
    dueText = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    if (daysUntil !== null) {
      if (daysUntil < 0)       dueText += ` · ${Math.abs(daysUntil)} days overdue`
      else if (daysUntil === 0) dueText += ' · Today'
      else                      dueText += ` · ${daysUntil} day${daysUntil === 1 ? '' : 's'}`
    }
  } else if (schedule.next_due_mileage) {
    dueText = `${Number(schedule.next_due_mileage).toLocaleString()} miles`
    if (milesUntil !== null) {
      dueText += milesUntil > 0
        ? ` · ${Number(milesUntil).toLocaleString()} miles away`
        : ' · Due now'
    }
  }

  return (
    <div className="flex items-center gap-3 px-5 py-3.5">
      <div className={clsx('w-2 h-2 rounded-full shrink-0', styles.dot)} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-[var(--text-primary)]">{name}</p>
        <p className="text-xs text-[var(--text-secondary)] mt-0.5">{dueText}</p>
      </div>
      <span className={clsx('px-2 py-0.5 rounded text-xs font-medium shrink-0', styles.badge)}>
        {styles.label}
      </span>
    </div>
  )
}

function VehicleScheduleSection({ vehicle }) {
  const navigate = useNavigate()
  const { data: schedules = [], isLoading } = useSchedules(vehicle.id)

  const sorted = [...schedules].sort((a, b) => {
    const rank = { overdue: 0, 'due-soon': 1, ok: 2, unknown: 3 }
    const sa = getScheduleStatus(a, vehicle.current_mileage).status
    const sb = getScheduleStatus(b, vehicle.current_mileage).status
    return (rank[sa] ?? 3) - (rank[sb] ?? 3)
  })

  return (
    <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border)]">
        <div className="flex items-center gap-3">
          <Plate registration={vehicle.registration} />
          <span className="font-medium text-[var(--text-primary)] text-sm">
            {vehicle.make} {vehicle.model}
          </span>
          {vehicle.year && (
            <span className="text-xs text-[var(--text-muted)]">{vehicle.year}</span>
          )}
        </div>
        <button
          onClick={() => navigate(`/vehicles/${vehicle.id}`)}
          className="flex items-center gap-1 text-xs text-indigo-400 hover:text-indigo-300 transition-colors"
        >
          Manage <ChevronRight size={13} />
        </button>
      </div>

      {isLoading ? (
        <div className="p-4 space-y-2 animate-pulse">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="h-10 bg-[var(--bg-overlay)] rounded-lg" />
          ))}
        </div>
      ) : sorted.length === 0 ? (
        <div className="px-5 py-4">
          <p className="text-sm text-[var(--text-muted)]">No schedule set up for this vehicle.</p>
        </div>
      ) : (
        <div className="divide-y divide-[var(--border)]">
          {sorted.map(s => (
            <ScheduleSummaryRow key={s.id} schedule={s} vehicle={vehicle} />
          ))}
        </div>
      )}
    </div>
  )
}

export default function ServiceSchedulePage() {
  const { data: vehicles = [], isLoading } = useVehicles()

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold text-[var(--text-primary)]">Service Schedule</h1>
        <p className="mt-1 text-sm text-[var(--text-secondary)]">
          Upcoming maintenance across all your vehicles.
        </p>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="h-40 bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl animate-pulse" />
          ))}
        </div>
      ) : vehicles.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-16 h-16 rounded-2xl bg-[var(--bg-surface)] border border-[var(--border)] flex items-center justify-center mb-4">
            <Calendar size={28} className="text-[var(--text-muted)]" />
          </div>
          <h3 className="font-display font-bold text-lg text-[var(--text-primary)] mb-2">No vehicles yet</h3>
          <p className="text-sm text-[var(--text-secondary)] max-w-xs">
            Add a vehicle first, then set up its service schedule from the vehicle detail page.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {vehicles.map(v => (
            <VehicleScheduleSection key={v.id} vehicle={v} />
          ))}
        </div>
      )}
    </div>
  )
}
